import React, { FC } from 'react';
import { Product } from 'shared/types/product/Product';

import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import { desktop, hugeDesktop, largeDesktop, minitablet, mobile, tablet } from 'helpers/utils/screensizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import ProductTile, { type ProductTileLayout } from 'components/commercetools-ui/organisms/product/product-tile';

import { SectionWithProductSliderContent } from './types';
import { useAdditionalCss } from '../helpers/useAdditionalCss';
import { useVisibleSlides } from '../helpers/useVisibleSlides';
import { SECTION_SPACING } from '../helpers/rendering';

/**
 * Hoisted so the same map drives both the carousel and the decision about whether arrows are any
 * use: they have to agree, and they stop agreeing the moment one of them is edited alone.
 */
const SLIDES_PER_VIEW = 1.2;
const SLIDER_BREAKPOINTS = {
  [mobile]: { slidesPerView: 2.1, spaceBetween: 16 },
  [tablet]: { slidesPerView: 2.1, spaceBetween: 16 },
  [desktop]: { slidesPerView: 3.5, spaceBetween: 16 },
  [largeDesktop]: { slidesPerView: 3.5, spaceBetween: 16 },
  [hugeDesktop]: { slidesPerView: 4.3, spaceBetween: 16 },
};

/**
 * The "Product IDs" carousel, rendered from the server-preloaded products.
 *
 * Ordering and variant selection happen once, in helpers/algolia/product-ids `productsInSkuOrder`:
 * each product arrives already mapped with the item-code the author listed selected, in the author's
 * order. The `compact` tile layout is the plain card this slider has always shown — a single image,
 * colour swatches and no RRP.
 */
export const ProductIDsSliderView: FC<{
  content: SectionWithProductSliderContent;
  products: Product[];
  tileLayout: ProductTileLayout;
}> = ({ content, products, tileLayout }) => {
  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);

  const visibleSlides = useVisibleSlides(SLIDES_PER_VIEW, SLIDER_BREAKPOINTS);

  const title = content?.title;
  const additional_css = content?.additional_css;
  const additional_css_class_names = content?.additional_css_class_names;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'ProductIDsProductSlider');

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div
        id="related-products"
        className={`${SECTION_SPACING} pl-24 md:pl-28 lg:pl-48 xl:pl-60 2xl:pl-80 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}
      >
        <div>
          {title && (
            <Typography id="related-products-title" className="pb-52 text-32 font-medium text-primary-black lg:pb-36 xl:text-36 2xl:text-48" as="h3">
              {title}
            </Typography>
          )}
          <div className="product-slider-container relative w-full">
            <Slider
              className="mb-32"
              slidesPerView={SLIDES_PER_VIEW}
              slidesPerGroup={1}
              dots={false}
              // Arrows only when there is something off-screen to reach. A section pinned to two
              // SKUs fits inside 4.3 visible slides on a desktop, so prev/next could not move it.
              arrows={products.length > visibleSlides && (isTabletSize || isDesktopSize || isMiniTabletSize)}
              innerArrows
              autoplay={false}
              solidArrows
              allowArrowsOnTouchDevice
              nextButtonStyles={{ top: '100%', left: 50 }}
              prevButtonStyles={{ top: '100%', left: 0 }}
              allowTouchMove
              spaceBetween={8}
              breakpoints={SLIDER_BREAKPOINTS}
            >
              {products.map((product, index) => (
                <div key={product.productId ?? product.slug ?? index} className="mb-48">
                  <ProductTile product={product} layout={tileLayout} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </>
  );
};
